import React from 'react';
import { Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { getStatusColor } from '../../../shared/utils/status';
import { normalizeCountry } from '../../../shared/utils/regions';
import AvailableIcon from '../../../assets/icons/AvailableIcon.svg';
import DegradedBadgeIcon from '../../../assets/icons/DegradedIcon.svg';

export const normalizeProductType = (pt) => Array.isArray(pt) ? pt[0] : pt;

export const ACTIVE_STATUSES = ['investigating', 'ongoing', 'in-progress', 'in progress', 'open'];

export const SEVERITY_RANK = { degraded: 1, disruption: 2 };

export const FILTER_CONTEXT_ID = 'StatusPageFilterBar';

export const PartialSeverityIcon = () => (
    <span style={{ position: 'relative', display: 'inline-block', width: 15, height: 15 }}>
        <img src={AvailableIcon} alt="" width="15" height="15" />
        <img
            src={DegradedBadgeIcon}
            alt="partial"
            width="8"
            height="8"
            style={{
                position: 'absolute',
                top: -2,
                right: -3,
                borderRadius: '50%',
            }}
        />
    </span>
);

export const tooltipSlotProps = {
    popper: {
        modifiers: [{
            name: 'offset',
            options: { offset: [0, -4] },
        }],
    },
    tooltip: {
        sx: {
            bgcolor: 'var(--ssmu-tooltip-bg)',
            color: 'var(--ssmu-tooltip-text)',
            fontSize: 12,
            fontFamily: 'Lato, sans-serif',
            borderRadius: '6px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.5)',
            p: '8px 12px',
        },
    },
    arrow: {
        sx: {
            color: 'var(--ssmu-tooltip-bg)',
        },
    },
};

const MAX_TOOLTIP_INCIDENTS = 5;

export const StatusIconTooltip = ({ children, worstStatus, incidents = [], hasClickAction }) => {
    if (!worstStatus) {
        return (
            <Tooltip title="Operational" arrow placement="bottom" slotProps={tooltipSlotProps}>
                {children}
            </Tooltip>
        );
    }

    const visible = incidents.slice(0, MAX_TOOLTIP_INCIDENTS);
    const overflowCount = incidents.length - visible.length;

    const content = (
        <div style={{ maxWidth: 280 }}>
            {visible.map((inc, i) => (
                <div key={i} style={{ marginBottom: i < visible.length - 1 ? 8 : 0 }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>{inc.title}</div>
                    <div style={{ display: 'flex', gap: 8, fontSize: 11, opacity: 0.8 }}>
                        <span style={{ color: getStatusColor(inc.severity) }}>
                            {inc.severity}
                        </span>
                        <span>|</span>
                        <span style={{ textTransform: 'capitalize' }}>{inc.status}</span>
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>
                        {dayjs(inc.created_at).format('MMM D, YYYY h:mm A')}
                    </div>
                </div>
            ))}
            {overflowCount > 0 && (
                <div style={{ opacity: 0.5, fontSize: 11, marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 4 }}>
                    +{overflowCount} more &mdash; click to view all
                </div>
            )}
            {overflowCount === 0 && hasClickAction && (
                <div style={{ opacity: 0.5, fontSize: 11, marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 4 }}>
                    Click to view details
                </div>
            )}
        </div>
    );

    return (
        <Tooltip title={content} arrow placement="bottom" slotProps={tooltipSlotProps}>
            {children}
        </Tooltip>
    );
};

export const isComponentAffected = (incident, componentName, targetPlane = null) => {
    const planes = incident?.Planes || incident?.planes;
    if (!planes) return false;
    if (targetPlane) {
        const components = planes[targetPlane]?.product_components || [];
        return components.includes(componentName);
    }
    const controlComponents = planes?.control?.product_components || [];
    if (controlComponents.includes(componentName)) return true;
    const dataComponents = planes?.data?.product_components || [];
    return dataComponents.includes(componentName);
};

export const getIncidentRegionCities = (incident) => {
    const planes = incident?.Planes || incident?.planes;
    if (!planes) return [];
    const cities = new Set();
    planes?.control?.regions?.forEach(r => { if (r.city) cities.add(r.city); });
    planes?.data?.regions?.forEach(r => { if (r.city) cities.add(r.city); });
    return [...cities];
};

export const isPartialRegionIncident = (incident, selectedRegionCities) => {
    if (!selectedRegionCities || selectedRegionCities.length <= 1) return false;
    const incidentCities = getIncidentRegionCities(incident);
    if (!incidentCities.length) return false;
    const affectedFilteredCities = selectedRegionCities.filter(c => incidentCities.includes(c));
    return affectedFilteredCities.length > 0 && affectedFilteredCities.length < selectedRegionCities.length;
};

export const resolveSelectedRegionCities = (pageFilters, regions, parseRegionFilterSelections) => {
    const selectedRegions = pageFilters?.region || [];
    if (!selectedRegions.length) return null;
    const parsed = parseRegionFilterSelections(selectedRegions);

    const cities = new Set(parsed.selectedCities);

    if (regions?.length && (parsed.selectedContinents.length || parsed.selectedCountries.length)) {
        regions.forEach(r => {
            if (!r.city) return;
            if (parsed.selectedContinents.includes(r.continent)) {
                cities.add(r.city);
            } else if (parsed.selectedCountries.includes(normalizeCountry(r.country))) {
                cities.add(r.city);
            }
        });
    }

    return cities.size > 0 ? [...cities] : null;
};
